// src/features/user/userApi.js

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { logout } from "../auth/authSlice";

/**
 * Base query configuration for RTK Query.
 * Sets the Authorization header using the token from Redux state.
 */
const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_SERVER,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

/**
 * Custom base query that wraps the original baseQuery to handle authentication errors.
 * Logs out the user automatically if a 401 Unauthorized error is returned.
 *
 * @param {any} args - Request arguments (URL or object with URL and options).
 * @param {object} api - RTK Query API object (contains dispatch, getState, etc.).
 * @param {object} extraOptions - Additional options passed to the base query.
 * @returns {Promise<object>} - The result of the query or mutation.
 */
const baseQueryWithLogout = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  console.log("result", result);

  if (result?.error?.originalStatus === 401) {
    // Dispatch logout action if unauthorized
    api.dispatch(logout());
  }

  return result;
};

/**
 * RTK Query API slice for user-related requests.
 * Provides auto-generated hooks for user endpoints.
 */
export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: baseQueryWithLogout,
  endpoints: (builder) => ({
    /**
     * GET /user/getUser
     * Fetches the currently authenticated user's data.
     *
     * @returns {object} User data
     */
    getUser: builder.query({
      query: () => "/user/getUser",
    }),
  }),
});

/**
 * Auto-generated React hook for the `getUser` endpoint.
 * @returns {object} { data, error, isLoading, refetch, ... }
 */
export const { useGetUserQuery } = userApi;
